import React, { useState, useEffect } from 'react'
import AuthService from 'tools/AuthService'
import request from 'tools/request'
import Button from 'components/shared/Button'
import { InputField } from 'components/forms/reduxForm'
import { Toast } from 'components/shared/Toaster'
import Drawer from 'components/shared/Drawer'
import SimplePagination from 'components/shared/SimplePagination.js/index'
import { getPaginationHeaders } from 'constants/utilityFunctions'
import Loader from 'components/shared/Loader'
import { Alert } from '@material-ui/lab'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import './TinVerification.scss'
import PrepareTooltip from "../../../../shared/Tooltip";
import CreditCost from 'components/shared/CreditCost'

const requestTinVerification = async (applicationId, payload) => {
    return await request(`applications/${applicationId}/sos/tin`, 'POST', payload)
}

const getTinVerifications = async (applicationId, pagination = {}) => {
    return await request(
        `applications/${applicationId}/sos/tin`,
        'GET',
        null,
        true,
        {
            'x-pagination-num': pagination.page || 0,
            'x-pagination-limit': pagination.limit || 5
        }
    )
}

const STATUS_CONFIG = {
    'TIN Matched': { color: 'var(--status-approved)', icon: CheckCircleIcon },
    'TIN Not Matched': { color: 'var(--status-declined)', icon: ErrorIcon },
}

const TinVerification = ({ applicationId, businessName, ein, creditCosts, creditUsageEnabled }) => {
    const [loading, setLoading] = useState(false)
    const [listLoading, setListLoading] = useState(false)
    const [verificationResult, setVerificationResult] = useState(null)
    const [verifications, setVerifications] = useState(null)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [tin, setTin] = useState(ein || '')
    const [name, setName] = useState(businessName || '')
    const [paginationParams, setPaginationParams] = useState({})
    const { isReadOnlyUser } = AuthService.getClientFeatures()

    useEffect(() => {
        if (ein) setTin(ein)
    }, [ein])

    useEffect(() => {
        if (businessName) setName(businessName)
    }, [businessName])

    useEffect(() => {
        fetchVerifications()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, paginationParams])

    const getCreditCost = () => {
        if (creditCosts) {
            const costObj = creditCosts.find(cost => cost.requestType === 'TIN_VERIFICATION')
            return costObj ? costObj.creditCost : 0
        }
        return 0
    }

    const fetchVerifications = () => {
        if (!applicationId) return
        setListLoading(true)
        getTinVerifications(applicationId, paginationParams)
            .then(res => {
                setVerifications(res)
            })
            .catch(err => {
                console.error('Error fetching TIN verifications:', err)
            })
            .finally(() => setListLoading(false))
    }

    const handleVerify = async () => {
        if (!tin || !name) return
        if (tin.replace(/[^0-9]/g, '').length !== 9) {
            Toast({ type: 'error', message: 'TIN must be exactly 9 digits.' })
            return
        }
        setLoading(true)
        try {
            const result = await requestTinVerification(applicationId, {
                tin,
                businessName: name
            })
            setVerificationResult(result)
            setDrawerOpen(true)
            Toast({ type: 'success', message: 'TIN verification completed.' })
            fetchVerifications()
        } catch (err) {
            console.error('TIN verification error:', err)
            Toast({ type: 'error', message: 'Failed to verify TIN. Please try again.' })
        } finally {
            setLoading(false)
        }
    }

    const handleViewResult = (item) => {
        setVerificationResult(item)
        setDrawerOpen(true)
    }

    const verificationsData = verifications?.response || []
    const paginationInfo = getPaginationHeaders(verifications)

    const getStatusConfig = (status) => STATUS_CONFIG[status] || { color: '#666', icon: ErrorIcon }

    return (
        <div className='m-3'>
            <div className='cardStyle mb-3'>
                <h5 className='mb-0'>TIN Verification</h5>
                <div className='tinFieldContainer'>
                    <div className='d-flex'>
                        <InputField
                            name='tin'
                            label='TIN / EIN'
                            required
                            id='tinInput'
                            input={{ value: tin, onChange: (e) => setTin(e.target.value) }}
                            disabled={isReadOnlyUser}
                        />
                    </div>
                    <div className='d-flex'>
                        <InputField
                            name='businessName'
                            label='Business Name'
                            required
                            id='tinBusinessName'
                            input={{ value: name, onChange: (e) => setName(e.target.value) }}
                            disabled={isReadOnlyUser}
                        />
                    </div>
                    <div className='d-flex align-items-center justify-content-end'>
                        {!isReadOnlyUser && (
                            <PrepareTooltip
                                title={!tin || !name ? 'Please fill in all fields' : tin.replace(/[^0-9]/g, '').length !== 9 ? 'TIN must be exactly 9 digits' : ''}
                            >
                                <Button
                                    onClick={handleVerify}
                                    loading={loading}
                                    id='perform-tin-verification'
                                    disabled={!tin || !name || tin.replace(/[^0-9]/g, '').length !== 9}
                                    endIcon={creditUsageEnabled && creditCosts ? <CreditCost cost={getCreditCost()} /> : undefined}
                                >
                                    Verify TIN
                                </Button>
                            </PrepareTooltip>
                        )}
                    </div>
                </div>
            </div>

            <div className='cardStyle'>
                <h5 className='mb-0'>Verification History</h5>
                {listLoading ? (
                    <Loader />
                ) : verificationsData.length > 0 ? (
                    <>
                        <div className='tin-list-header'>
                            <span>Business Name</span>
                            <span>TIN</span>
                            <span>Status</span>
                            <span></span>
                        </div>
                        {verificationsData.map((item, idx) => {
                            const config = getStatusConfig(item.status)
                            return (
                                <div key={idx} className='tin-list-row'>
                                    <span>{item.name}</span>
                                    <span>{item.tin}</span>
                                    <PrepareTooltip title={item.message || ''}>
                                        <span className='tin-status-badge' style={{ background: config.color }}>
                                            {item.status}
                                        </span>
                                    </PrepareTooltip>
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        onClick={() => handleViewResult(item)}
                                    >
                                        View
                                    </Button>
                                </div>
                            )
                        })}
                    </>
                ) : (
                    <Alert severity="info" className='mt-3'>No TIN verifications found.</Alert>
                )}
                {paginationInfo?.totalRecords > 0 && (
                    <SimplePagination
                        onChangePagination={setPaginationParams}
                        totalRecords={paginationInfo.totalRecords}
                        totalPages={paginationInfo.totalPages}
                    />
                )}
            </div>

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title='TIN Verification Result'
                width='small'
            >
                {verificationResult && (
                    <div className='tin-drawer-content'>
                        <div className='tin-drawer-status' style={{ borderLeftColor: getStatusConfig(verificationResult.status).color }}>
                            {React.createElement(getStatusConfig(verificationResult.status).icon, { style: { color: getStatusConfig(verificationResult.status).color, fontSize: 32 } })}
                            <div>
                                <h6 className='mb-0'>{verificationResult.status}</h6>
                                <small>{verificationResult.irsReason}</small>
                            </div>
                        </div>

                        {verificationResult.message && (
                            <div className='tin-drawer-message'>
                                <Alert severity="warning">{verificationResult.message}</Alert>
                            </div>
                        )}

                        <div className='tin-drawer-details'>
                            <div className='tin-drawer-row'>
                                <span className='tin-drawer-label'>Business Name</span>
                                <span className='tin-drawer-value'>{verificationResult.name}</span>
                            </div>
                            <div className='tin-drawer-row'>
                                <span className='tin-drawer-label'>TIN</span>
                                <span className='tin-drawer-value'>{verificationResult.tin}</span>
                            </div>
                            <div className='tin-drawer-row'>
                                <span className='tin-drawer-label'>Status</span>
                                <span className='tin-drawer-value'>{verificationResult.status}</span>
                            </div>
                            <div className='tin-drawer-row'>
                                <span className='tin-drawer-label'>IRS Code</span>
                                <span className='tin-drawer-value'>{verificationResult.irsCode}</span>
                            </div>
                            <div className='tin-drawer-row'>
                                <span className='tin-drawer-label'>IRS Reason</span>
                                <span className='tin-drawer-value'>{verificationResult.irsReason}</span>
                            </div>
                            {verificationResult.irsServiceStatus && (
                                <div className='tin-drawer-row'>
                                    <span className='tin-drawer-label'>IRS Service Status</span>
                                    <span className='tin-drawer-value'>{verificationResult.irsServiceStatus}</span>
                                </div>
                            )}
                            {verificationResult.lastIRSCheckDate && (
                                <div className='tin-drawer-row'>
                                    <span className='tin-drawer-label'>Last IRS Check Date</span>
                                    <span className='tin-drawer-value'>{verificationResult.lastIRSCheckDate}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Drawer>
        </div>
    )
}

export default TinVerification